import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { MESSAGES, LANGS } from "./messages";

const LangContext = createContext(null);

export { LANGS };

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("melilux_lang") : null;
    return saved && MESSAGES[saved] ? saved : "ro";
  });

  useEffect(() => {
    localStorage.setItem("melilux_lang", lang);
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (MESSAGES[next]) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = MESSAGES[lang] || MESSAGES.en;
      let s = dict[key] ?? MESSAGES.en[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          s = s.split(`{${k}}`).join(String(v));
        });
      }
      return s;
    },
    [lang]
  );

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

export function useT() {
  return useContext(LangContext).t;
}

const SITE_NAME = "Melilux";

/**
 * Sets the browser-tab title as "<title> — Melilux". Pass an already-translated
 * string (t("meta.title.about"), a product name, …) so it follows the active language.
 * Falsy values keep the plain site name — useful while a page is still loading.
 */
export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
  }, [title]);
}

/**
 * <Trans tKey="..." vars={{ year: 2026 }} />
 * Renders a message string with:
 *  - <gold>...</gold> → italic brushed-gold span
 *  - \n → <br />
 */
export function Trans({ tKey, vars }) {
  const { t } = useContext(LangContext);
  const text = t(tKey, vars);
  const out = [];
  const regex = /<gold>([\s\S]*?)<\/gold>|\n/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[0] === "\n") {
      out.push(<br key={`b-${i++}`} />);
    } else {
      out.push(
        <em key={`g-${i++}`} className="serif-italic text-melilux-gold">
          {m[1]}
        </em>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}
